import { Fragment, useEffect } from 'react';
import {
  type Control,
  FieldPath,
  FieldValues,
  type FormState,
  type UseFormSetError,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { SelectField, TextInputField } from '@brighte/form-components';
import { Text } from '@spark-web/text';
import FieldArray from '../../components/FieldArray/FieldArray';
import { LABELS, OWNERSHIP_DETAILS } from '../../utils';
import { generateOptionsFromMaxCount } from './helpers';

const fieldArrayDefault = {
  firstName: '',
  middleName: '',
  lastName: '',
};

type DirectorProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  formState: FormState<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
};

function Director<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  watch,
  formState,
  setError,
  setValue,
}: Readonly<DirectorProps<TFieldValues>>) {
  const { NUMBER_OF_DIRECTORS, DIRECTOR, FIRST_NAME, MIDDLE_NAME, LAST_NAME } = LABELS;
  const { NUM_OF_DIRECTOR_DESCRIPTION } = OWNERSHIP_DETAILS;
  const directorDropdown = 'company.numOfDirectors';
  const directorList = watch(name);
  const errors = formState?.errors;

  useEffect(() => {
    const errorField = (errors as any).enterprise?.company?.directors;
    if (errorField?.message) {
      setError(directorDropdown as FieldPath<TFieldValues>, errorField);
    }
  }, [errors, setError]);

  useEffect(() => {
    if (directorList?.length > 0) {
      setValue?.(directorDropdown as FieldPath<TFieldValues>, directorList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FieldArray name={name} control={control} defaultValues={fieldArrayDefault}>
      {({ handleChange, fields }) => (
        <>
          <SelectField
            name={directorDropdown as FieldPath<TFieldValues>}
            control={control}
            label={NUMBER_OF_DIRECTORS}
            options={generateOptionsFromMaxCount(2)}
            fieldProps={{
              description: NUM_OF_DIRECTOR_DESCRIPTION,
            }}
            onChange={handleChange}
            placeholder="Select number of directors"
          />
          {fields.length > 0
            ? fields.map((item, index) => {
                return (
                  <Fragment key={item.id}>
                    <Text tone="muted" weight="semibold">
                      {`${DIRECTOR} #${index + 1}`}
                    </Text>
                    <TextInputField
                      name={`${name}.${index}.firstName` as FieldPath<TFieldValues>}
                      label={FIRST_NAME}
                      control={control}
                    />
                    <TextInputField
                      name={`${name}.${index}.middleName` as FieldPath<TFieldValues>}
                      label={MIDDLE_NAME}
                      control={control}
                      FieldProps={{
                        secondaryLabel: '(Optional)',
                      }}
                    />
                    <TextInputField
                      name={`${name}.${index}.lastName` as FieldPath<TFieldValues>}
                      label={LAST_NAME}
                      control={control}
                    />
                  </Fragment>
                );
              })
            : null}
        </>
      )}
    </FieldArray>
  );
}

export default Director;
